import React, { useEffect } from 'react';
import OktaAuth, {
  AccessToken,
  OktaAuthOptions,
  TokenManagerError,
  toRelativeUrl,
} from '@okta/okta-auth-js';
import { SecureRoute, Security } from '@okta/okta-react';
import type { RestoreOriginalUriFunction } from '@okta/okta-react/bundles/types/OktaContext';
import { ReactNode, Suspense } from 'react';
import { BrowserRouter, Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { Logger, PageLoader, useLogger } from '@cko/dashboard-shared';
import { datadogRum } from '@datadog/browser-rum';
import { History } from 'history';

import { OKTA_CONFIG } from '../../environment/okta';
import { CustomLoginCallback, Expired, Error } from './components';
import { useIdle, useCheckHasExpired } from './hooks';
import { ROUTES } from './Routes';
import { DATADOG_EVENTS } from './dataDogActions';

const navigateToExpiredPage = (history: History) => {
  const { origin } = window.location;
  const path = window.location.href.substring(origin.length);
  if (!path.startsWith('/expired')) {
    const originalUri = `/expired?original_path=${encodeURIComponent(path)}`;
    history.push(originalUri);
  }
};

const authOptions: OktaAuthOptions = {
  issuer: OKTA_CONFIG.issuer,
  clientId: OKTA_CONFIG.clientID,
  redirectUri: OKTA_CONFIG.redirectUri,
  postLogoutRedirectUri: OKTA_CONFIG.postLogoutRedirectUri,
  tokenManager: {
    secure: true,
    storage: 'sessionStorage',
  },
  services: {
    autoRenew: true,
    autoRemove: true,
  },
  pkce: true,
};

let oktaAuth: OktaAuth | undefined = undefined;

const getAuthClient = (logger: Logger) => {
  if (oktaAuth !== undefined) {
    return oktaAuth;
  } else {
    oktaAuth = new OktaAuth(authOptions);

    oktaAuth.start();

    oktaAuth.tokenManager.on('error', (error: TokenManagerError) => {
      logger.track(DATADOG_EVENTS.error, { error });
    });

    oktaAuth.tokenManager.on('expired', () => {
      logger.track(DATADOG_EVENTS.expired);
    });

    oktaAuth.tokenManager.on('removed', () => {
      logger.track(DATADOG_EVENTS.removed);
    });

    oktaAuth.tokenManager.on('renewed', () => {
      logger.track(DATADOG_EVENTS.renewed);
    });

    oktaAuth.tokenManager.on('set_storage', () => {
      logger.track(DATADOG_EVENTS.setStorage);
    });

    oktaAuth.tokenManager.on('added', (key, token) => {
      logger.track(DATADOG_EVENTS.added);
      if (key === 'accessToken') {
        const accessToken = token as AccessToken;
        const userRoles = (accessToken.claims.cko_user_roles as string[]) ?? [];
        const user = {
          id: accessToken.claims.cko_user_id as string,
          client_id: accessToken.claims.cko_client_id,
          user_type: accessToken.claims.cko_user_type,
          user_roles: userRoles.join(', '),
        };
        datadogRum.setUser(user);
      }
    });

    return oktaAuth;
  }
};

const ONE_HOUR_IN_MILLISECONDS = 60 * 60 * 1000;
const FIVE_MINUTES_IN_MILLISECONDS = 5 * 60 * 1000;

const SecureContainer = ({ children }: { children: ReactNode }) => {
  const history = useHistory();
  const logger = useLogger();
  const authClient = getAuthClient(logger);
  useCheckHasExpired(FIVE_MINUTES_IN_MILLISECONDS, authClient, () => {
    navigateToExpiredPage(history);
  });

  useIdle(ONE_HOUR_IN_MILLISECONDS, () => {
    authClient.signOut();
    authClient.stop();
  });

  const restoreOriginalUri: RestoreOriginalUriFunction = async (
    _: OktaAuth,
    originalUri: string,
  ) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  return (
    <Security oktaAuth={authClient} restoreOriginalUri={restoreOriginalUri}>
      {children}
    </Security>
  );
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const logger = useLogger();

  useEffect(() => {
    logger.track(DATADOG_EVENTS.newProvider);
  }, [logger]);

  return (
    <Suspense fallback={<PageLoader />}>
      <BrowserRouter>
        <SecureContainer>
          <Switch>
            <Route path={ROUTES.callback} exact>
              <CustomLoginCallback errorComponent={(errorProps) => <Error {...errorProps} />} />
            </Route>

            <Route path={ROUTES.expired}>
              <Expired />
            </Route>
            <Route path={ROUTES.login}>
              <Redirect to={ROUTES.home} />
            </Route>
            <SecureRoute path={ROUTES.home}>{children}</SecureRoute>
          </Switch>
        </SecureContainer>
      </BrowserRouter>
    </Suspense>
  );
};
